/**
 * Die Weiche: Wer kommt wohin.
 *
 *   /pruefung/<id>?zugang=…   die Prüfung — der Beirat über seinen Link,
 *                             die Hausverwaltung über ihre Anmeldung
 *   /verwaltung …             die Verwaltungsseite (Anmeldung nötig)
 *   /admin                    der Betreiber
 *   /anmelden, /passwort/<t>  Anmeldung und Passwort setzen
 *   /                         zur Verwaltung, oder zur Anmeldung
 *
 * Ohne Router-Bibliothek: Sechs Wege rechtfertigen keine Abhängigkeit.
 * Der Pfad wird gelesen, der Verlauf mit pushState gepflegt.
 */

import { useCallback, useEffect, useState } from 'react';

import { Pruefungsansicht, Rahmen } from './seiten/Pruefungsansicht';
import { Anmeldung } from './seiten/Anmeldung';
import { Verwaltung, type VerwaltungsBereich } from './seiten/verwaltung/Verwaltung';
import { api, ApiFehler, mandantWaehlen, pruefungIdAusUrl, zugangToken } from './lib/api';
import { DEMO } from './demo/modus';
import type { Konto } from './lib/typen';

interface Weg {
  art: 'pruefung' | 'verwaltung' | 'anmelden' | 'passwort' | 'start';
  pruefungId?: string | null;
  bereich?: VerwaltungsBereich;
  token?: string;
}

function wegLesen(): Weg {
  const pfad = window.location.pathname;
  if (DEMO) return { art: 'pruefung', pruefungId: pruefungIdAusUrl() };
  if (pfad.startsWith('/pruefung/')) return { art: 'pruefung', pruefungId: pruefungIdAusUrl() };
  if (pfad.startsWith('/passwort/')) return { art: 'passwort', token: decodeURIComponent(pfad.slice('/passwort/'.length)) };
  if (pfad === '/anmelden') return { art: 'anmelden' };
  if (pfad === '/admin') return { art: 'verwaltung', bereich: 'mandanten' };
  if (pfad.startsWith('/verwaltung')) {
    const rest = pfad.slice('/verwaltung'.length);
    if (rest === '/neu') return { art: 'verwaltung', bereich: 'neu' };
    if (rest === '/einstellungen') return { art: 'verwaltung', bereich: 'einstellungen' };
    const treffer = /^\/pruefung\/([0-9a-f-]{36})/i.exec(rest);
    if (treffer) return { art: 'verwaltung', bereich: 'pruefung', pruefungId: treffer[1] };
    return { art: 'verwaltung', bereich: 'pruefungen' };
  }
  return { art: 'start' };
}

export function App() {
  const [weg, setWeg] = useState<Weg>(wegLesen);
  const [konto, setKonto] = useState<Konto | null | undefined>(undefined);

  useEffect(() => {
    const beiVerlauf = () => setWeg(wegLesen());
    window.addEventListener('popstate', beiVerlauf);
    return () => window.removeEventListener('popstate', beiVerlauf);
  }, []);

  const navigieren = useCallback((pfad: string) => {
    window.history.pushState({}, '', pfad);
    setWeg(wegLesen());
    window.scrollTo({ top: 0 });
  }, []);

  // Wer bin ich? Einmal fragen; 401 heißt: niemand.
  useEffect(() => {
    if (DEMO || weg.art === 'passwort') {
      setKonto(null);
      return;
    }
    api.konto
      .lesen()
      .then(setKonto)
      .catch((g) => {
        if (g instanceof ApiFehler && g.status === 401) setKonto(null);
        else setKonto(null);
      });
  }, [weg.art]);

  function angemeldet(k: Konto) {
    setKonto(k);
    if (k.benutzer.systemadmin && !k.mandant) {
      mandantWaehlen(null);
      navigieren('/admin');
    } else {
      navigieren('/verwaltung');
    }
  }

  // ── Die Prüfung ──────────────────────────────────────────
  if (weg.art === 'pruefung') {
    if (!weg.pruefungId) {
      return (
        <Rahmen>
          <div className="leer card">
            <h3>Dieser Link ist unvollständig</h3>
            <p>
              Er sollte die Form <code className="mono">/pruefung/&lt;id&gt;?zugang=…</code> haben. Bitte öffnen Sie den Link aus
              Ihrer Einladungsmail noch einmal.
            </p>
          </div>
        </Rahmen>
      );
    }
    return (
      <Pruefungsansicht
        key={weg.pruefungId}
        pruefungId={weg.pruefungId}
        onVerwaltung={konto && !zugangToken() ? () => navigieren(`/verwaltung/pruefung/${weg.pruefungId}`) : undefined}
      />
    );
  }

  // ── Passwort setzen ──────────────────────────────────────
  if (weg.art === 'passwort') {
    return <Anmeldung passwortToken={weg.token} onAngemeldet={angemeldet} />;
  }

  // ── Alles andere braucht die Antwort auf „Wer bin ich?" ──
  if (konto === undefined) {
    return (
      <Rahmen>
        <div className="skelett" style={{ height: 120 }} />
      </Rahmen>
    );
  }

  if (!konto) {
    if (weg.art === 'start' && zugangToken()) {
      return (
        <Rahmen>
          <div className="leer card">
            <h3>Dieser Link ist unvollständig</h3>
            <p>Bitte öffnen Sie den Link aus Ihrer Einladungsmail unverändert.</p>
          </div>
        </Rahmen>
      );
    }
    return <Anmeldung onAngemeldet={angemeldet} />;
  }

  if (weg.art === 'start' || weg.art === 'anmelden') {
    // Angemeldet: ab in die Verwaltung. Ohne Neuladen, nur die Adresse.
    window.history.replaceState({}, '', konto.benutzer.systemadmin && !konto.mandant ? '/admin' : '/verwaltung');
    return (
      <Verwaltung
        konto={konto}
        bereich={konto.benutzer.systemadmin && !konto.mandant ? 'mandanten' : 'pruefungen'}
        pruefungId={null}
        onNavigieren={navigieren}
        onKontoAktualisiert={setKonto}
        onAbgemeldet={() => {
          setKonto(null);
          navigieren('/anmelden');
        }}
      />
    );
  }

  return (
    <Verwaltung
      konto={konto}
      bereich={weg.bereich ?? 'pruefungen'}
      pruefungId={weg.pruefungId ?? null}
      onNavigieren={navigieren}
      onKontoAktualisiert={setKonto}
      onAbgemeldet={() => {
        setKonto(null);
        navigieren('/anmelden');
      }}
    />
  );
}
